<script setup lang="ts">
/* =============================================================
   SEARCH CONTROLS
   =============================================================
   Where, which ship, when, and what it needs -- the four inputs
   of §4 of the brief, in one bar. Shared by the search screen and
   the berth profile, so "change the dates" works the same in both.

   It holds no state of its own: every change goes straight out as
   a patch to the search context, which lives in the URL.

   Picking a vessel fills the amenities with that ship's service
   needs when none are chosen yet -- the brief's point that a
   registered ship already knows what it requires -- but never
   overwrites a choice the user has made.
   ============================================================= */

import { computed } from 'vue'
import SelectField from '@/components/ui/SelectField.vue'
import TextField from '@/components/ui/TextField.vue'
import AmenityPicker from '@/components/ui/AmenityPicker.vue'
import { fromLocalInput, toLocalInput } from '@/lib/bookingLabels'
import type { SearchContext } from '@/config/routes'
import type { AmenityCode, BerthCity, Vessel } from '@/lib/types'

const props = withDefaults(
  defineProps<{
    context: SearchContext
    vessels: Vessel[]
    /** Omit to hide the place field -- the berth profile already has a place. */
    cities?: BerthCity[]
  }>(),
  { cities: undefined },
)

const emit = defineEmits<{ update: [patch: Partial<SearchContext>] }>()

const cityValue = computed(() => props.context.city ?? '')
const vesselValue = computed(() => (props.context.vessel ? String(props.context.vessel) : ''))
const fromValue = computed(() => (props.context.from ? toLocalInput(props.context.from) : ''))
const toValue = computed(() => (props.context.to ? toLocalInput(props.context.to) : ''))
const amenityValue = computed(() => (props.context.amenities ?? []) as AmenityCode[])

function onVessel(value: string): void {
  const id = value ? Number(value) : undefined
  const vessel = props.vessels.find((v) => v.id === id)
  const patch: Partial<SearchContext> = { vessel: id }
  if (vessel && amenityValue.value.length === 0) patch.amenities = [...vessel.service_needs]
  emit('update', patch)
}

/* Choosing an arrival with no departure yet proposes one twelve hours later:
   a typical day call, and it puts a price on screen at once. */
function onFrom(value: string): void {
  const from = fromLocalInput(value) ?? undefined
  const patch: Partial<SearchContext> = { from }
  if (from && !props.context.to) {
    patch.to = new Date(new Date(from).getTime() + 12 * 3_600_000).toISOString()
  }
  emit('update', patch)
}

function onTo(value: string): void {
  emit('update', { to: fromLocalInput(value) ?? undefined })
}

const vesselLabel = (v: Vessel) => `${v.name} · ${v.length_m} m`
</script>

<template>
  <div class="controls">
    <div class="row" :class="{ noPlace: !cities }">
      <SelectField
        v-if="cities"
        label="Where"
        placeholder="Choose a place"
        :model-value="cityValue"
        @update:model-value="emit('update', { city: $event || undefined })"
      >
        <option v-for="c in cities" :key="`${c.city}-${c.country_code}`" :value="c.city">
          {{ c.city }} ({{ c.berths }})
        </option>
      </SelectField>

      <SelectField label="Vessel" :model-value="vesselValue" @update:model-value="onVessel">
        <option value="">Any vessel</option>
        <option v-for="v in vessels" :key="v.id" :value="String(v.id)">{{ vesselLabel(v) }}</option>
      </SelectField>

      <TextField label="Arrival" type="datetime-local" :model-value="fromValue" @update:model-value="onFrom" />
      <TextField label="Departure" type="datetime-local" :model-value="toValue" @update:model-value="onTo" />
    </div>

    <AmenityPicker
      label="Needs"
      :model-value="amenityValue"
      @update:model-value="emit('update', { amenities: $event })"
    />
  </div>
</template>

<style scoped>
/* Sized by its own width, not the window's: the same bar sits full width on
   the search page and in a narrow column on the berth and booking pages. */
.controls {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.row {
  display: grid;
  gap: var(--space-sm);
  grid-template-columns: minmax(10rem, 1fr) minmax(12rem, 1.3fr) minmax(12rem, 1fr) minmax(12rem, 1fr);
  align-items: start;
}

.row.noPlace {
  grid-template-columns: minmax(12rem, 1.3fr) minmax(12rem, 1fr) minmax(12rem, 1fr);
}

@container (max-width: 56rem) {
  .row,
  .row.noPlace {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container (max-width: 28rem) {
  .row,
  .row.noPlace {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
